import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarDays,
  ChevronDown,
  Download,
  Info,
  MoreVertical,
  Plus,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  CAMPAIGN_FILTER_OPTIONS,
  DEFAULT_DATE_RANGE,
  ENGAGEMENT_LEGEND,
  MOCK_ENGAGEMENT_FUNNEL,
  MOCK_PERFORMANCE_SUMMARY,
  MOCK_TOP_EMAIL_ROWS,
  PERFORMANCE_METRIC_OPTIONS,
  buildMockMetricSeries,
  formatDateRangeLabel,
  formatExecutionDate,
  formatNumber,
  getChartTitle,
  getClickRateDisplay,
  getEngagementBarWidth,
  getMetricSummaryDisplay,
  getOpenRateDisplay,
  getQuickRangeDays,
  getTableSubtitle,
  parseIsoDate,
  sortRowsByMetric,
  toIsoDate,
} from '../../utils/automationStatisticsUtils';

const QUICK_RANGE_OPTIONS = [
  { id: '7', label: 'Last 7 days', days: 7 },
  { id: '30', label: 'Last 30 days', days: 30 },
  { id: '60', label: 'Last 60 days', days: 60 },
  { id: '90', label: 'Last 90 days', days: 90 },
];

const PERFORMANCE_CARDS = [
  { id: 'delivered', label: 'Email Delivered', key: 'delivered' },
  { id: 'bounced', label: 'Bounced', key: 'bounced' },
  { id: 'unsubscribed', label: 'Unsubscribed', key: 'unsubscribed' },
  { id: 'spam', label: 'Spam Complaints', key: 'spamComplaints' },
];

const MetricInfoLabel = ({ label }) => (
  <span className="inline-flex items-center gap-1 text-sm font-medium text-slate-600">
    {label}
    <Info className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
  </span>
);

const EngagementFunnelChart = ({ rows }) => {
  const maxValue = Math.max(...rows.map((row) => row.value), 1);

  return (
    <div className="space-y-4" data-testid="engagement-summary-chart">
      {rows.map((row) => (
        <div key={row.id} className="grid grid-cols-[88px_1fr_72px] items-center gap-4">
          <span className="text-sm text-slate-600">{row.label}</span>
          <div className="relative h-8 rounded-md bg-slate-100">
            <div
              className="absolute inset-y-0 left-0 rounded-md bg-violet-400/80"
              style={{ width: `${getEngagementBarWidth(row.value, maxValue)}%` }}
            />
          </div>
          <div className="flex justify-end">
            <span className="rounded-sm bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
              {row.cumulativePercent}%
            </span>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-5 pt-2">
        {ENGAGEMENT_LEGEND.map((item) => (
          <div key={item.id} className="flex items-center gap-2 text-xs text-slate-500">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};

const MetricTrendChart = ({ data, metricId }) => {
  const isCountMetric = metricId === 'email_sent';

  return (
    <div className="h-[280px] w-full" data-testid="automation-metric-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="metricAreaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: '#64748b', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            label={{ value: 'Dates', position: 'insideBottom', offset: -2, fill: '#64748b', fontSize: 12 }}
          />
          <YAxis
            tick={{ fill: '#64748b', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => (isCountMetric ? formatNumber(value) : `${value}%`)}
            label={{
              value: isCountMetric ? 'Email Sent' : 'Open Rate',
              angle: -90,
              position: 'insideLeft',
              fill: '#64748b',
              fontSize: 12,
            }}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              borderColor: '#e2e8f0',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
            }}
          />
          <Legend
            verticalAlign="bottom"
            height={36}
            iconType="circle"
            wrapperStyle={{ fontSize: 12, color: '#64748b' }}
          />
          <Area
            type="monotone"
            dataKey="value"
            name="All Campaigns"
            stroke="#8b5cf6"
            fill="url(#metricAreaFill)"
            strokeWidth={2}
            dot={false}
          />
          <Area
            type="monotone"
            dataKey="emailCampaign"
            name="Email Campaign"
            stroke="#1e40af"
            fill="transparent"
            strokeWidth={1.5}
            dot={false}
          />
          <Area
            type="monotone"
            dataKey="workflowCampaign"
            name="Workflow Campaign"
            stroke="#7c3aed"
            fill="transparent"
            strokeWidth={1.5}
            dot={false}
          />
          <Area
            type="monotone"
            dataKey="bulkActionCampaign"
            name="Bulk Action Campaign"
            stroke="#38bdf8"
            fill="transparent"
            strokeWidth={1.5}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const DateRangePopover = ({
  open,
  onOpenChange,
  draftRange,
  onDraftRangeChange,
  onConfirm,
}) => {
  const selectedRange = useMemo(() => ({
    from: parseIsoDate(draftRange.start),
    to: parseIsoDate(draftRange.end),
  }), [draftRange.end, draftRange.start]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex min-w-[280px] items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm hover:bg-slate-50"
          data-testid="automation-statistics-date-trigger"
        >
          <span className="inline-flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-slate-400" />
            {formatDateRangeLabel(draftRange.start, draftRange.end)}
          </span>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto max-w-none border-slate-200 bg-white p-0 shadow-xl"
        data-testid="automation-statistics-date-popover"
      >
        <div className="p-4">
          <Calendar
            mode="range"
            numberOfMonths={2}
            defaultMonth={parseIsoDate('2026-04-01')}
            selected={selectedRange}
            onSelect={(range) => {
              onDraftRangeChange({
                start: range?.from ? toIsoDate(range.from) : draftRange.start,
                end: range?.to ? toIsoDate(range.to) : draftRange.start,
              });
            }}
          />
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {QUICK_RANGE_OPTIONS.map((option) => (
              <Button
                key={option.id}
                type="button"
                variant="outline"
                size="sm"
                className="border-slate-200 text-slate-600"
                data-testid={`automation-statistics-quick-range-${option.id}`}
                onClick={() => onDraftRangeChange(getQuickRangeDays(option.days))}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <Button
            type="button"
            size="sm"
            className="bg-blue-600 text-white hover:bg-blue-700"
            data-testid="automation-statistics-date-confirm"
            onClick={onConfirm}
          >
            Confirm
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const AutomationStatisticsPanel = ({ stats: _stats }) => {
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [selectedMetricId, setSelectedMetricId] = useState('open_rate');
  const [showStatisticsInNumbers, setShowStatisticsInNumbers] = useState(false);
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [draftDateRange, setDraftDateRange] = useState(DEFAULT_DATE_RANGE);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const kebabRef = useRef(null);

  useEffect(() => {
    if (datePopoverOpen) {
      setDraftDateRange(dateRange);
    }
  }, [datePopoverOpen, dateRange]);

  const summary = MOCK_PERFORMANCE_SUMMARY;
  const sortedRows = useMemo(
    () => sortRowsByMetric(MOCK_TOP_EMAIL_ROWS, selectedMetricId),
    [selectedMetricId],
  );
  const chartSeries = useMemo(
    () => buildMockMetricSeries(selectedMetricId, dateRange.start, dateRange.end),
    [dateRange.end, dateRange.start, selectedMetricId],
  );
  const metricSummary = useMemo(
    () => getMetricSummaryDisplay(selectedMetricId, summary, showStatisticsInNumbers),
    [selectedMetricId, showStatisticsInNumbers, summary],
  );

  const handleConfirmDateRange = () => {
    setDateRange(draftDateRange);
    setDatePopoverOpen(false);
  };

  return (
    <div className="space-y-6" data-testid="automation-statistics-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger
              className="w-[180px] border-slate-200 bg-white text-slate-700"
              data-testid="automation-statistics-campaign-filter"
            >
              <SelectValue placeholder="All Campaigns" />
            </SelectTrigger>
            <SelectContent>
              {CAMPAIGN_FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DateRangePopover
            open={datePopoverOpen}
            onOpenChange={setDatePopoverOpen}
            draftRange={draftDateRange}
            onDraftRangeChange={setDraftDateRange}
            onConfirm={handleConfirmDateRange}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="border-slate-200 text-slate-600"
            data-testid="automation-statistics-export"
            aria-label="Export statistics"
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            className="bg-blue-600 text-white hover:bg-blue-700"
            data-testid="automation-statistics-create-campaign"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create campaign
          </Button>
          <Button
            ref={kebabRef}
            type="button"
            variant="outline"
            size="icon"
            className="border-slate-200 text-slate-600"
            data-testid="automation-statistics-more"
            aria-label="More actions"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-slate-900">Engagement summary</h2>
          <p className="mt-1 text-sm text-slate-500">
            Performance summary of recipient engagement, including open rates, click activity, and conversions.
          </p>
        </div>
        <EngagementFunnelChart rows={MOCK_ENGAGEMENT_FUNNEL} />
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-slate-900">Performance Analysis</h2>
          <p className="mt-1 text-sm text-slate-500">
            Track campaign performance trends for a metric over time.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {PERFORMANCE_CARDS.map((card) => (
            <div
              key={card.id}
              className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
              data-testid={`automation-performance-card-${card.id}`}
            >
              <MetricInfoLabel label={card.label} />
              <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-900">
                {formatNumber(summary[card.key])}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <h2 className="text-lg font-semibold text-slate-900" data-testid="automation-statistics-chart-title">
            {getChartTitle(selectedMetricId)}
          </h2>
          <Select value={selectedMetricId} onValueChange={setSelectedMetricId}>
            <SelectTrigger
              className="w-[180px] border-slate-200 bg-white"
              data-testid="automation-statistics-metric-select"
            >
              <SelectValue placeholder="Open Rate" />
            </SelectTrigger>
            <SelectContent>
              {PERFORMANCE_METRIC_OPTIONS.map((option) => (
                <SelectItem
                  key={option.id}
                  value={option.id}
                  data-testid={`automation-statistics-metric-${option.id}`}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-6">
            <MetricInfoLabel label={metricSummary.label} />
            <p className="mt-4 text-4xl font-semibold text-slate-900" data-testid="automation-statistics-summary-value">
              {metricSummary.value}
            </p>
            <div className="mt-8 space-y-1 text-sm text-slate-500">
              {metricSummary.footer.map((item) => (
                <p key={item.label}>
                  {item.label}: {item.value}
                </p>
              ))}
            </div>
          </div>
          <MetricTrendChart data={chartSeries} metricId={selectedMetricId} />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Top performing emails</h2>
            <p className="mt-1 text-sm text-slate-500" data-testid="automation-statistics-table-subtitle">
              {getTableSubtitle(selectedMetricId)}
            </p>
          </div>
          <Select value={selectedMetricId} onValueChange={setSelectedMetricId}>
            <SelectTrigger
              className="w-[180px] border-slate-200 bg-white"
              data-testid="automation-statistics-table-metric-select"
            >
              <SelectValue placeholder="Open Rate" />
            </SelectTrigger>
            <SelectContent>
              {PERFORMANCE_METRIC_OPTIONS.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3 px-6 py-4">
          <Switch
            id="automation-statistics-show-numbers"
            checked={showStatisticsInNumbers}
            onCheckedChange={setShowStatisticsInNumbers}
            data-testid="automation-statistics-show-numbers"
          />
          <Label htmlFor="automation-statistics-show-numbers" className="text-sm text-slate-700">
            Show statistics in numbers
          </Label>
        </div>

        <div className="overflow-x-auto px-2 pb-2">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Title</TableHead>
                <TableHead>Execution Date</TableHead>
                <TableHead>Delivered</TableHead>
                <TableHead>Open Rate</TableHead>
                <TableHead>Click Rate</TableHead>
                <TableHead>Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((row) => (
                <TableRow key={row.id} data-testid={`automation-statistics-email-row-${row.id}`}>
                  <TableCell className="font-medium text-slate-900">{row.title}</TableCell>
                  <TableCell className="text-slate-600">{formatExecutionDate(row.executionDate)}</TableCell>
                  <TableCell>{formatNumber(row.delivered)}</TableCell>
                  <TableCell data-testid={`automation-open-rate-${row.id}`}>
                    {getOpenRateDisplay(row, showStatisticsInNumbers)}
                  </TableCell>
                  <TableCell data-testid={`automation-click-rate-${row.id}`}>
                    {getClickRateDisplay(row, showStatisticsInNumbers)}
                  </TableCell>
                  <TableCell className="text-slate-600">{row.type}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
};

export default AutomationStatisticsPanel;
